import { useEffect, useRef, useCallback } from 'react';
import { useStore } from '@/store/useStore';

const DAY_MAP: Record<number, string> = {
  0: 'zo', 1: 'ma', 2: 'di', 3: 'wo', 4: 'do', 5: 'vr', 6: 'za',
};

// Parse time string "HH:MM" to { hours, minutes }
function parseTime(time: string): { hours: number; minutes: number } {
  const [hours, minutes] = time.split(':').map(Number);
  return { hours, minutes };
}

// Request notification permission
export async function requestNotificationPermission(): Promise<boolean> {
  if (!('Notification' in window)) {
    console.warn('This browser does not support notifications');
    return false;
  }

  if (Notification.permission === 'granted') {
    return true;
  }

  if (Notification.permission === 'denied') {
    return false;
  }

  const permission = await Notification.requestPermission();
  return permission === 'granted';
}

// Show a notification
function showNotification(title: string, body: string, subtitle?: string) {
  if (Notification.permission !== 'granted') return;

  try {
    const basePath = import.meta.env?.BASE_URL || '/';

    const fullBody = subtitle ? `${body}\n${subtitle}` : body;

    const notification = new Notification(title, {
      body: fullBody,
      icon: `${basePath}images/icon-192.png`,
      badge: `${basePath}images/icon-192.png`,
      tag: 'easyhabits-reminder',
      requireInteraction: true, // Keep notification visible until user interacts
    } as NotificationOptions);

    // Click handler - focus/open the app and go to coach
    notification.onclick = () => {
      window.focus();
      window.location.href = '/coach';
      notification.close();
    };
  } catch (e) {
    console.warn('Failed to show notification:', e);
  }
}

// Test notification
export function sendTestNotification() {
  showNotification('🔔 EasyHabits', 'Test notification - click to open the app!');
}

export function useNotifications() {
  const { settings, progress, templates } = useStore();
  const checkIntervalRef = useRef<number | null>(null);
  const lastNotificationRef = useRef<string | null>(null);

  const checkAndNotify = useCallback(() => {
    if (!settings.notifications || Notification.permission !== 'granted') {
      return;
    }

    const now = new Date();
    const currentHours = now.getHours();
    const currentMinutes = now.getMinutes();
    const today = now.toISOString().split('T')[0];
    const dayCode = DAY_MAP[now.getDay()];

    // Get selected templates and their habits
    const selectedTemplates = templates.filter((t) =>
      (progress.selectedTemplates || []).includes(t.id)
    );
    const allHabits = selectedTemplates.flatMap((t) => t.habits);

    // Check each habit for reminder
    for (const habit of allHabits) {
      const custom = progress.customHabits?.[habit.id];

      // Skip disabled habits
      if (custom?.disabled) continue;

      // Skip one-time habits
      const isOneTime = custom?.oneTime ?? habit.oneTime ?? false;
      if (isOneTime) continue;

      // Check if scheduled for today
      const effectiveDays = custom?.days || habit.days || ['ma', 'di', 'wo', 'do', 'vr', 'za', 'zo'];
      if (!effectiveDays.includes(dayCode)) continue;

      // Skip already completed or skipped
      const isCompleted = progress.completions.some(
        (c) => c.habitId === habit.id && c.date === today && c.completed
      );
      if (isCompleted) continue;

      const isSkipped = (progress.skippedHabits?.[habit.id] || []).includes(today);
      if (isSkipped) continue;

      // Check reminder settings
      const reminder = custom?.reminder ?? 'none';
      if (reminder === 'none') continue;

      // Get the target time
      // Note: null means explicitly set to "all-day", undefined means use default
      const targetTime = custom?.targetTime !== undefined ? custom.targetTime : habit.targetTime;

      let notifyTime: { hours: number; minutes: number } | null = null;

      if (targetTime) {
        // Habit has a specific time
        const parsed = parseTime(targetTime);

        if (reminder === 'before') {
          // 15 minutes before
          notifyTime = {
            hours: parsed.hours,
            minutes: parsed.minutes - 15,
          };
          if (notifyTime.minutes < 0) {
            notifyTime.minutes += 60;
            notifyTime.hours -= 1;
          }
        } else if (reminder === 'at') {
          notifyTime = parsed;
        } else if (reminder === 'after') {
          // 15 minutes after
          notifyTime = {
            hours: parsed.hours,
            minutes: parsed.minutes + 15,
          };
          if (notifyTime.minutes >= 60) {
            notifyTime.minutes -= 60;
            notifyTime.hours += 1;
          }
        }
      } else {
        // All-day habit - use reminderTime from customization or settings
        const reminderTime = custom?.reminderTime ?? settings.reminderTime;
        if (reminderTime) {
          notifyTime = parseTime(reminderTime);
        }
      }

      if (!notifyTime) continue;

      // Check if it's time to notify (within 1 minute window)
      if (
        currentHours === notifyTime.hours &&
        currentMinutes === notifyTime.minutes
      ) {
        // Prevent duplicate notifications
        const notificationKey = `${habit.id}-${today}-${notifyTime.hours}:${notifyTime.minutes}`;
        if (lastNotificationRef.current === notificationKey) continue;

        lastNotificationRef.current = notificationKey;

        // Get localized habit name and description
        const lang = settings.language;
        const habitName = habit.name[lang] || habit.name.en;
        const description = habit.description?.[lang] || habit.description?.en;
        const subtitle = description || (lang === 'nl' ? 'Tijd voor je gewoonte!' : lang === 'fr' ? 'C\'est l\'heure!' : 'Time for your habit!');

        showNotification(
          `${habit.icon} ${habitName}`,
          subtitle
        );
      }
    }
  }, [settings, progress, templates]);

  // Set up interval to check every minute
  useEffect(() => {
    if (!settings.notifications) {
      if (checkIntervalRef.current) {
        clearInterval(checkIntervalRef.current);
        checkIntervalRef.current = null;
      }
      return;
    }

    // Check immediately
    checkAndNotify();

    // Then check every minute
    checkIntervalRef.current = window.setInterval(checkAndNotify, 60000);

    return () => {
      if (checkIntervalRef.current) {
        clearInterval(checkIntervalRef.current);
      }
    };
  }, [settings.notifications, checkAndNotify]);

  return {
    requestPermission: requestNotificationPermission,
    isSupported: 'Notification' in window,
    permission: 'Notification' in window ? Notification.permission : 'denied',
  };
}
